<template>
  <DialogFull :data="dialogSettings" @close="close">
    <template v-slot:dialog.body>
      <div class="container col-lg-8 col-sm-12 my-10">
        <h2>{{ dialogSettings.title }}</h2>
        <p class="secondary-text">
          Modifying the approval chain could remove any existing approval chain
        </p>
        <ValidationObserver ref="approvalChainForm">
          <ValidationProvider
            v-for="(approvalChainLevel, index) in approvalChainTemplateLevels"
            :key="index"
            v-slot="{ errors }"
            rules="required"
          >
            <Select
              :label="`Level ${approvalChainLevel.level}`"
              item-text="label"
              item-value="id"
              :items="formattedUsers"
              :loading="isFetchingUsers"
              :errorMessages="getErrorMessages(errors)"
              :value="approvalChainLevel.users"
              :appendOuterIcon="
                isLastIndex(index) && index !== 0 ? `mdi-close` : null
              "
              @change="
                $event =>
                  handleUpdateApprovers(approvalChainLevel.level, $event)
              "
              @clickOuterIcon="removeLevel(approvalChainLevel.level)"
              autocomplete
              multiple
              chips
            >
              <template v-slot:selection="{ data }">
                <v-chip>
                  {{ data.item.label }}
                </v-chip>
              </template>
            </Select>
          </ValidationProvider>
          <PrimaryButton full-width light @click.native="addLevel">
            Add Approval Level
            <v-icon>mdi-plus</v-icon>
          </PrimaryButton>
        </ValidationObserver>
      </div>
    </template>
    <template v-slot:dialog.action>
      <v-spacer />
      <PrimaryButton @click.native="save" :loading="isLoading">
        Save
      </PrimaryButton>
    </template>
  </DialogFull>
</template>

<script>
import DialogFull from "@/components/common/DialogFull";
import {
  UPDATE_BOOKING_APPROVERS,
  FETCH_CURRENT_BOOKING,
  BOOKINGS_NAMESPACE
} from "@/store/modules/bookings/actions";
import { createNamespacedHelpers } from "vuex";
import PrimaryButton from "@/components/common/Button/PrimaryButton";
import Select from "@/components/common/Select";
import { ValidationProvider, ValidationObserver } from "vee-validate";
import { FETCH_USERS } from "@/store/modules/users/actions";
import { USERS_NAMESPACE } from "@/store/modules/users";
import { copyState } from "@/utils/helpers";
import { getFullName } from "@/utils/users";
import { filter, find, map, reduce, size } from "lodash";

const { mapActions } = createNamespacedHelpers(BOOKINGS_NAMESPACE);
const {
  mapActions: mapUserActions,
  mapState: mapUserState
} = createNamespacedHelpers(USERS_NAMESPACE);

export default {
  components: {
    PrimaryButton,
    DialogFull,
    Select,
    ValidationProvider,
    ValidationObserver
  },
  props: {
    isOpen: Boolean,
    approvers: Array
  },
  inject: ["currentBookingId"],
  created() {
    this.setApprovalChainLevels();
    const filter = {
      role: ["client booking approver"]
    };
    this.fetchUsers({ filter, page: { size: 10000 } });
  },
  data() {
    return {
      isLoading: false,
      approvalChainTemplateLevels: [{ level: 1, users: [] }]
    };
  },
  computed: {
    ...mapUserState(copyState(["users", "isFetchingUsers"])),
    dialogSettings() {
      return {
        title: "Edit Role Approval Chain",
        dialog: this.isOpen
      };
    },
    formattedUsers() {
      return map(this.users, user => {
        return { ...user, label: getFullName(user) };
      });
    }
  },
  methods: {
    ...mapActions({
      updateBookingApprovers: UPDATE_BOOKING_APPROVERS,
      fetchBooking: FETCH_CURRENT_BOOKING
    }),
    ...mapUserActions({ fetchUsers: FETCH_USERS }),
    handleChange(editedInformation) {
      this.internalInformation = editedInformation;
    },
    close() {
      this.$emit("close");
    },
    setApprovalChainLevels() {
      this.approvalChainTemplateLevels = reduce(
        this.approvers,
        (grouped, approver) => {
          const { levelNbr, user } = approver;
          const groupedByLevel = find(grouped, { level: levelNbr });
          if (!groupedByLevel) {
            grouped.push({ level: levelNbr, users: user ? [user.id] : [] });
          } else {
            grouped = map(grouped, groupApprovers => {
              if (groupApprovers.level === levelNbr) {
                groupApprovers.users = user
                  ? [...groupApprovers.users, user.id]
                  : groupApprovers.users;
              }
              return groupApprovers;
            });
          }
          return grouped;
        },
        []
      );
    },
    getApprovers() {
      let approvers = [];
      map(this.approvalChainTemplateLevels, approvalChainTemplateLevel => {
        const { users, level } = approvalChainTemplateLevel;
        map(users, user => {
          approvers.push({ level, user_id: user });
        });
      });
      return approvers;
    },
    async save() {
      this.isLoading = true;
      this.showValidationErrors = true;
      const isValid = await this.$refs.approvalChainForm.validate();
      if (isValid) {
        await this.updateBookingApprovers({
          id: this.currentBookingId,
          approvers: this.getApprovers()
        });
        this.fetchBooking(this.currentBookingId);
      }
      this.isLoading = true;
    },
    getErrorMessages(errors) {
      return this.showValidationErrors ? errors : "";
    },
    handleUpdateApprovers(level, users) {
      this.approvalChainTemplateLevels = map(
        this.approvalChainTemplateLevels,
        approvalChainTemplateLevel => {
          if (approvalChainTemplateLevel.level === level) {
            approvalChainTemplateLevel.users = users;
          }
          return approvalChainTemplateLevel;
        }
      );
    },
    addLevel() {
      const level = size(this.approvalChainTemplateLevels) + 1;
      this.approvalChainTemplateLevels = [
        ...this.approvalChainTemplateLevels,
        {
          level,
          users: []
        }
      ];
    },
    isLastIndex(index) {
      return index == size(this.approvalChainTemplateLevels) - 1;
    },
    removeLevel(levelToBeRemoved) {
      this.approvalChainTemplateLevels = filter(
        this.approvalChainTemplateLevels,
        ({ level }) => level !== levelToBeRemoved
      );
    }
  }
};
</script>
